import { Shield, Lock, Eye, User, FileText, CheckCircle, Mail, Phone } from 'lucide-react'

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
      {/* Hero */}
      <div className="relative bg-gradient-to-r from-purple-600 to-pink-500 text-white py-14 md:py-20">
        <div className="absolute inset-0 bg-black/10" />
        <div className="container-spa max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-full mb-4 shadow-lg">
            <Shield className="w-8 h-8 md:w-10 md:h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-3 drop-shadow-sm">
            Privacy Policy
          </h1>
          <p className="text-lg md:text-xl text-white/95 max-w-2xl mx-auto">
            Your privacy matters. Here is how Spa~Radise Luxe protects and uses your information.
          </p>
          <p className="text-sm text-white/80 mt-3">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="container-spa max-w-4xl mx-auto px-4 py-10 pb-16">
        {/* Introduction */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Introduction</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            At Spa~Radise Luxe, we are committed to protecting your privacy and the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website or our spa and wellness services.
          </p>
          <p className="text-gray-700 leading-relaxed">
            By using our services, you agree to the collection and use of information in line with this policy. We do not sell, rent, or share your personal information with third parties for marketing purposes without your explicit consent.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <Eye className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-gray-800">Information We Collect</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <User className="w-5 h-5 text-purple-500" />
                Personal information
              </h3>
              <p className="text-gray-700 mb-2 text-sm">When you book a service or get in touch, we may collect:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-700 text-sm ml-2">
                <li>Name and contact details (email, phone)</li>
                <li>Address for service delivery</li>
                <li>Payment information (handled securely by our payment provider)</li>
                <li>Booking history and preferences</li>
                <li>Gift card purchases and redemption details</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-pink-500" />
                Automatically collected information
              </h3>
              <p className="text-gray-700 mb-2 text-sm">When you visit our site, we may automatically collect:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-700 text-sm ml-2">
                <li>Device type and browser</li>
                <li>IP address and general location</li>
                <li>Usage and navigation patterns</li>
                <li>Cookies and similar technologies</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How We Use Your Information */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <Lock className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-gray-800">How We Use Your Information</h2>
          </div>

          <ul className="space-y-4">
            {[
              { title: 'Service delivery', text: 'To process bookings, schedule appointments, and deliver spa services to your location.' },
              { title: 'Communication', text: 'To send confirmations, reminders, and respond to your inquiries.' },
              { title: 'Payment processing', text: 'To process payments and manage gift card transactions securely.' },
              { title: 'Personalization', text: 'To improve your experience and tailor service recommendations.' },
              { title: 'Marketing (with consent)', text: 'To send offers and updates only if you opt in.' },
              { title: 'Website improvement', text: 'To analyze usage and improve the site and experience.' },
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold text-gray-800">{item.title}</span>
                  <span className="text-gray-700"> — {item.text}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Information Sharing */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Information Sharing and Disclosure</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We do not sell or rent your personal information. We may share it only when:
          </p>
          <ul className="space-y-3 text-gray-700">
            {[
              'With trusted service providers (payment, delivery, IT) under strict confidentiality.',
              'When required by law or to protect rights, property, or safety.',
              'In connection with a business transfer (e.g. merger or sale of assets).',
              'When you give us explicit consent to share with specific parties.',
            ].map((text, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Data Security */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Data Security</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We use industry-standard measures to protect your information, including:
          </p>
          <ul className="space-y-2 text-gray-700 mb-4">
            {[
              'SSL encryption for data in transit',
              'Secure handling of payment data',
              'Regular security reviews and updates',
              'Limited, need-to-know access to personal data',
              'Certified payment gateways',
            ].map((text, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
          <p className="text-gray-600 text-sm leading-relaxed">
            No internet or electronic storage method is 100% secure. We use commercially reasonable measures to protect your information but cannot guarantee absolute security.
          </p>
        </section>

        {/* Your Rights */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Your Rights and Choices</h2>
          <p className="text-gray-700 mb-4">You have the right to:</p>
          <ul className="space-y-2 text-gray-700 mb-4">
            {[
              { label: 'Access', text: 'Request a copy of the personal information we hold about you.' },
              { label: 'Correction', text: 'Update or correct inaccurate information.' },
              { label: 'Deletion', text: 'Request deletion of your data (subject to legal obligations).' },
              { label: 'Opt-out', text: 'Unsubscribe from marketing at any time.' },
              { label: 'Restrict processing', text: 'Ask us to limit how we use your information.' },
              { label: 'Data portability', text: 'Request your data in a structured, portable format.' },
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <span><strong className="text-gray-800">{item.label}:</strong> {item.text}</span>
              </li>
            ))}
          </ul>
          <p className="text-gray-700 text-sm">
            To exercise these rights, contact us using the details at the bottom of this page.
          </p>
        </section>

        {/* Cookies */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Cookies and Tracking</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We use cookies and similar technologies to improve the site, analyze traffic, and personalize content. You can control cookies in your browser; disabling them may affect how the site works.
          </p>
          <p className="text-gray-700 font-semibold mb-2">Types we use:</p>
          <ul className="space-y-1.5 text-gray-700 text-sm">
            <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" /><span><strong>Essential</strong> — needed for the site to work</span></li>
            <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" /><span><strong>Analytics</strong> — to understand how visitors use the site</span></li>
            <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" /><span><strong>Preference</strong> — to remember your settings</span></li>
          </ul>
        </section>

        {/* Children's Privacy */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Children's Privacy</h2>
          <p className="text-gray-700 leading-relaxed">
            Our services are not aimed at anyone under 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has given us personal information, please contact us and we will delete it.
          </p>
        </section>

        {/* Changes to Policy */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Changes to This Policy</h2>
          <p className="text-gray-700 leading-relaxed">
            We may update this Privacy Policy from time to time. We will post the new version on this page and update the "Last updated" date. Your continued use of our services after changes means you accept the updated policy.
          </p>
        </section>

        {/* Contact */}
        <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg p-8 md:p-10 mb-8 border border-purple-200">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Contact Us</h2>
          <p className="text-gray-700 mb-4">
            For questions, concerns, or requests about this Privacy Policy or your data:
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 text-gray-700">
            <a href="mailto:sparadiseluxe@gmail.com" className="inline-flex items-center gap-3 text-purple-700 hover:text-purple-800 font-medium">
              <Mail className="w-5 h-5 flex-shrink-0" />
              sparadiseluxe@gmail.com
            </a>
            <a href="https://wa.me/233537813579" className="inline-flex items-center gap-3 text-purple-700 hover:text-purple-800 font-medium" target="_blank" rel="noopener noreferrer">
              <Phone className="w-5 h-5 flex-shrink-0" />
              WhatsApp: 053 781 3579
            </a>
            <a href="tel:+233202468529" className="inline-flex items-center gap-3 text-purple-700 hover:text-purple-800 font-medium">
              <Phone className="w-5 h-5 flex-shrink-0" />
              Call: 020 246 8529
            </a>
          </div>
        </section>

        {/* Footer note */}
        <p className="text-center text-gray-600 text-sm">
          Thank you for trusting Spa~Radise Luxe with your personal information. Your privacy and security are important to us.
        </p>
      </div>
    </div>
  )
}

export default Privacy
